import { collection, Collection } from "typesaurus"

// High level rules for created DB keys. This is done so we can easily switch backingstores.
//
// Collection properties:
//  * must be lowercase
//  * will use '_' in between words
//  * will match the type name if the type isn't a Key (sting) Type
//    e.g. MetaType is a string type, therefore a property containing would be just meta.
//    and CamelCased Types like MulitpleChoice would be done as multiple_choice.
//  * For properties that contain arrays, the will be named in the plural.
//  * for id's which are specific to the collection, it will include the collection name.
//    e.g. an ID specific to the snap collection would be named snap_id
//  * id's which may refer to mulitple collections will be named id and will have an accompanying
//    type identifier which includes the collection Type reference
//

// Collections
import {
  Asset,
  Media,
  BlockInteraction,
  BlockQtiInteraction,
  BlockText,
  BlockVideo,
  BlockPresentation,
  BlockImage,
  BlockPdf,
  BlockEmpty,
  ElementCourseState,
  SeqAssessment,
  SeqConfig,
  SeqExercise,
  SeqLogic,
  SeqScreen,
  SnapCourse,
  Snap,
  SnapShot,
  SnapState,
  User,
  WordBank,
  assetReducer,
  blockInteractionReducer,
  blockQtiInteractionReducer,
  blockEmptyReducer,
  blockImageReducer,
  blockTextReducer,
  blockPdfReducer,
  blockPresentationReducer,
  blockVideoReducer,
  elementCourseStateReducer,
  libraryReducer,
  seqScreenReducer,
  seqAssessmentReducer,
  seqConfigReducer,
  seqExerciseReducer,
  seqLogicReducer,
  snapCourseReducer,
  snapReducer,
  snapShotReducer,
  snapStateReducer,
  userReducer,
  wordBankReducer,
  SeqQtiScreen,
  seqQtiScreenReducer,
  SeqPassage,
  seqPassageReducer
} from "./collections"

import {
  TypeDispatchedActions,
  GetNodefn,
  TypeActions,
  TypeTypes,
  TypeModels
} from "./reducers"

export type CollectionModels =
  | Asset
  | Media
  | BlockInteraction
  | BlockText
  | BlockVideo
  | BlockPresentation
  | BlockImage
  | BlockPdf
  | BlockEmpty
  | ElementCourseState
  | SeqConfig
  | SeqExercise
  | SeqLogic
  | SeqScreen
  | Snap
  | SnapCourse
  | SnapShot
  | SnapState
  | User
  | WordBank

export type CollectionTypes = {
  assets: Asset
  library: Media
  block_interactions: BlockInteraction
  block_qti_interactions: BlockQtiInteraction
  block_texts: BlockText
  block_videos: BlockVideo
  block_presentations: BlockPresentation
  block_images: BlockImage
  block_pdfs: BlockPdf
  block_empty: BlockEmpty
  element_course_state: ElementCourseState
  seq_assessments: SeqAssessment
  seq_configs: SeqConfig
  seq_exercises: SeqExercise
  seq_logics: SeqLogic
  seq_screens: SeqScreen
  seq_qti_screens: SeqQtiScreen
  seq_passages: SeqPassage
  snap_courses: SnapCourse
  snaps: Snap
  snap_shots: SnapShot
  snap_state: SnapState
  users: User
  word_banks: WordBank
}

export type CollectionNames = keyof CollectionTypes

export type KeysOfType<TTarget, TValue> = {
  [K in keyof TTarget]: TTarget[K] extends TValue ? K : never
}[keyof TTarget]

export function collectionGuard<Model>(
  key: KeysOfType<CollectionTypes, Model>
): Collection<Model> {
  return collection<Model>(key)
}

export const db = {
  assets: collectionGuard<Asset>("assets"),
  library: collectionGuard<Media>("library"),
  block_interactions: collectionGuard<BlockInteraction>("block_interactions"),
  block_qti_interactions: collectionGuard<BlockQtiInteraction>(
    "block_qti_interactions"
  ),
  block_presentations: collectionGuard<BlockPresentation>(
    "block_presentations"
  ),
  block_texts: collectionGuard<BlockText>("block_texts"),
  block_videos: collectionGuard<BlockVideo>("block_videos"),
  block_images: collectionGuard<BlockImage>("block_images"),
  block_pdfs: collectionGuard<BlockPdf>("block_pdfs"),
  block_empty: collectionGuard<BlockEmpty>("block_empty"),
  element_course_state: collectionGuard<ElementCourseState>(
    "element_course_state"
  ),
  seq_assessments: collectionGuard<SeqAssessment>("seq_assessments"),
  seq_configs: collectionGuard<SeqConfig>("seq_configs"),
  seq_exercises: collectionGuard<SeqExercise>("seq_exercises"),
  seq_logics: collectionGuard<SeqLogic>("seq_logics"),
  seq_screens: collectionGuard<SeqScreen>("seq_screens"),
  seq_qti_screens: collectionGuard<SeqQtiScreen>("seq_qti_screens"),
  seq_passages: collectionGuard<SeqPassage>("seq_passages"),
  snap_courses: collectionGuard<SnapCourse>("snap_courses"),
  snaps: collectionGuard<Snap>("snaps"),
  snap_shots: collectionGuard<SnapShot>("snap_shots"),
  snap_state: collectionGuard<SnapState>("snap_state"),
  users: collectionGuard<User>("users"),
  word_banks: collectionGuard<WordBank>("word_banks")
}

export const reducers = {
  assets: assetReducer,
  library: libraryReducer,
  block_interactions: blockInteractionReducer,
  block_qti_interactions: blockQtiInteractionReducer,
  block_presentations: blockPresentationReducer,
  block_texts: blockTextReducer,
  block_videos: blockVideoReducer,
  block_images: blockImageReducer,
  block_pdfs: blockPdfReducer,
  block_empty: blockEmptyReducer,
  element_course_state: elementCourseStateReducer,
  seq_assessments: seqAssessmentReducer,
  seq_configs: seqConfigReducer,
  seq_exercises: seqExerciseReducer,
  seq_logics: seqLogicReducer,
  seq_qti_screens: seqQtiScreenReducer,
  seq_passages: seqPassageReducer,
  seq_screens: seqScreenReducer,
  snap_courses: snapCourseReducer,
  snaps: snapReducer,
  snap_shots: snapShotReducer,
  snap_state: snapStateReducer,
  users: userReducer,
  word_banks: wordBankReducer
}

export type DaoNode<K extends keyof TypeActions> = TypeDispatchedActions<
  K extends keyof TypeActions ? K : never
> & { node: GetNodefn<TypeTypes[K] extends TypeModels ? TypeTypes[K] : never> }
export { default } from "./dao"
export type { DaoState } from "./dao"
